import { Clock, Inbox } from 'lucide-react';
import type { ResponseStats } from '../../services/usageApi';
import { StatTile } from './StatTile';
import { TimeSeriesChart } from './TimeSeriesChart';
import { RankedBars } from './RankedBars';
import { SERIES_ERROR, SERIES_PRIMARY, formatCompact, formatHours, formatPercent } from './chartTokens';
import { BaselineCompare } from './BaselineCompare';

/**
 * Inbound candidate questions and how fast they were answered.
 *
 * Reads real triage records rather than analytics events, so a dropped event cannot quietly remove
 * a slow reply from the sample. Every figure is team-wide: per-recruiter response times would make
 * this a performance review nobody agreed to.
 */
export function ResponseSection({ stats }: { stats: ResponseStats }) {
  const { totals } = stats;

  if (totals.inbound === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-8 text-center">
        <Inbox className="mx-auto h-8 w-8 text-zinc-300" />
        <p className="mt-3 text-sm font-medium text-zinc-700">No inbound requests in this range</p>
        <p className="mx-auto mt-1 max-w-md text-xs text-zinc-500">
          Requests are recorded from the moment a thread first appears in the Candidate requests
          tab. Threads handled before that was switched on are not counted.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Inbound requests" value={formatCompact(totals.inbound)} hint="Candidate questions received" />
        {/* The real answer leads: received → SENT. Time-to-draft sits beside it as the internal
            half, because the two are genuinely different numbers and conflating them would flatter
            the team by however long a draft sat unsent. */}
        <StatTile
          label="Median time to reply"
          value={formatHours(totals.medianReplyHours)}
          hint={
            totals.repliesObserved === 0
              ? 'No sends observed yet'
              : `Received → sent · ${formatCompact(totals.repliesObserved)} measured`
          }
        />
        <StatTile
          label="Slowest 10% to reply"
          value={formatHours(totals.p90ReplyHours)}
          hint="p90 — the tail candidates notice"
        />
        <StatTile
          label="Median time to draft"
          value={formatHours(totals.medianHours)}
          hint="Our half: received → drafted"
        />
        {/* Named "Drafted", not "Answered": it measures drafts, and now that the actual send is
            measured beside it, calling drafts "answered" would overstate what reached anyone. */}
        <StatTile
          label="Drafted"
          value={totals.draftedRate === null ? '—' : formatPercent(totals.draftedRate)}
          hint={`${totals.awaiting} still awaiting a draft`}
        />
      </div>

      {/* Opt-in, collapsed: the two reply figures above are the ones with a measured "before". */}
      <BaselineCompare
        medianReplyHours={totals.medianReplyHours}
        p90ReplyHours={totals.p90ReplyHours}
        repliesObserved={totals.repliesObserved}
      />

      <section className="rounded-xl border border-zinc-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-zinc-900">Inbound per day</h3>
        <p className="mt-0.5 text-xs text-zinc-500">
          When candidates wrote in, by the day they sent it — not the day it was read.
        </p>
        <div className="mt-4">
          <TimeSeriesChart
            points={stats.daily.map((d) => ({ date: d.date, value: d.inbound }))}
            color={SERIES_PRIMARY}
            seriesLabel="Inbound requests"
          />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        {stats.byCategory.length > 0 && (
          <section className="rounded-xl border border-zinc-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-zinc-900">What they ask about</h3>
            <p className="mt-0.5 text-xs text-zinc-500">
              Question category, as classified when the request was triaged.
            </p>
            <div className="mt-4">
              <RankedBars
                items={stats.byCategory.map((c) => ({ label: c.label, value: c.inbound }))}
                color={SERIES_PRIMARY}
              />
            </div>
          </section>
        )}

        {/*
          The policy turned into a product finding.
          Compensation, visa, relocation and rejection-reason questions are classified out BEFORE a
          model is called, so a salary question never reaches a prompt. That is correct and stays.
          But the SIZE of this pile is a different fact: if a fifth of inbound is one topic nobody
          may auto-answer, the answer belongs on the careers page rather than in an inbox forty
          times a month. Nothing else in the app was counting it.
        */}
        <section className="rounded-xl border border-zinc-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-zinc-900">Questions we never auto-answer</h3>
          <p className="mt-0.5 text-xs text-zinc-500">
            {totals.refusedRate === null
              ? 'Topics policy holds back for a human.'
              : `${formatPercent(totals.refusedRate)} of inbound (${formatCompact(totals.refused)}). Policy holds these back for a human — a big pile here is a gap in what candidates can find out for themselves.`}
          </p>
          <div className="mt-4">
            <RankedBars
              items={stats.refusedCategories.map((c) => ({ label: c.label, value: c.inbound }))}
              color={SERIES_ERROR}
              emptyMessage="Nothing was held back in this range."
            />
          </div>
        </section>
      </div>

      {/* The measurement's own boundary, stated where the numbers are read rather than buried in a
          doc. Someone will otherwise quote "median time to reply" in a review, and it is not that. */}
      <p className="flex items-start gap-1.5 text-xs text-zinc-400">
        <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          <strong className="font-medium text-zinc-500">Time to reply</strong> is the real send,
          read back from the thread. The tab does send the reply itself now, but the send time is
          still observed on a later queue load rather than recorded as it happens, so coverage stays
          partial: a thread dismissed before the queue next loads is not read again, and its reply is
          never seen. That under-counts, never over-counts.{' '}
          <strong className="font-medium text-zinc-500">Time to draft</strong> is the half
          this tool owns and is measured for every request. Team-wide figures only.
          {totals.excludedNegative > 0 &&
            ` ${totals.excludedNegative} record(s) had a draft timestamp before the email arrived and are excluded.`}
          {stats.truncated && ' Scan hit its ceiling, so counts are a floor.'}
        </span>
      </p>
    </div>
  );
}
